import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { adminApi, getAdminToken } from "@/lib/adminApi";
import usePostSeo from "@/hooks/usePostSeo";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

/**
 * BlogPost page — supports two render modes:
 *  - Public: /blog/:slug — fetches published post + further reading.
 *  - Preview: /blog/preview/:id — fetches ANY post (draft or published) via
 *    admin JWT and shows an amber PREVIEW MODE banner up top.
 */
export default function BlogPost({ preview = false }) {
  const params = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, post: null, further: [], error: null });

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        if (preview) {
          if (!getAdminToken()) {
            if (alive) setState({ loading: false, post: null, further: [], error: "admin_required" });
            return;
          }
          const { data } = await adminApi.get(`/admin/blog/posts/id/${params.id}`);
          if (alive) setState({ loading: false, post: data.post, further: [], error: null });
        } else {
          const { data } = await api.get(`/blog/posts/${params.slug}`);
          if (alive) setState({ loading: false, post: data.post, further: data.further_reading || [], error: null });
          // Session-deduped view increment.
          try {
            const key = `regulens_blog_viewed_${params.slug}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, "1");
              api.post(`/blog/posts/${params.slug}/view`).catch(() => {});
            }
          } catch { /* sessionStorage may be blocked */ }
        }
      } catch (e) {
        if (alive) setState({ loading: false, post: null, further: [], error: e?.response?.status === 404 ? "not_found" : "error" });
      }
    }
    load();
    return () => { alive = false; };
  }, [preview, params.slug, params.id]);

  const post = state.post;
  const canonical = post ? `${window.location.origin}/blog/${post.slug}` : "";

  usePostSeo({
    title: post ? `${post.title} — Regulens` : "Regulens Blog",
    description: post?.excerpt || "",
    canonical,
    ogType: "article",
    article: post ? { title: post.title, author: post.author, publish_date: post.publish_date, url: canonical } : null,
  });

  if (state.loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16 text-[var(--rg-muted)] text-sm" data-testid="blog-post-loading">
          Loading…
        </div>
      </Layout>
    );
  }

  if (state.error === "admin_required") {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post-admin-required">
          <p className="text-[var(--rg-text-strong)]">Admin session required to preview drafts.</p>
          <Button className="mt-4" onClick={() => navigate("/admin/login")}>Go to Admin Login</Button>
        </div>
      </Layout>
    );
  }

  if (!post || state.error === "not_found") {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post-not-found">
          <h1 className="text-[var(--rg-text-strong)] text-3xl font-bold" style={{ fontFamily: "'DM Sans', sans-serif" }}>Post not found</h1>
          <p className="mt-3 text-[var(--rg-text)]">The post you're looking for doesn't exist or hasn't been published yet.</p>
          <Link to="/blog"><Button className="mt-6">Back to Blog</Button></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {preview && (
        <div
          className="w-full text-center py-2 px-4 border-b border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] text-[var(--rg-amber)] font-semibold text-sm"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
          data-testid="blog-preview-banner"
        >
          PREVIEW MODE — This post is not yet published
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-post-article">
        <div className="blog-content">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-[var(--rg-sage)] hover:text-[var(--rg-muted)] font-semibold text-sm mb-8"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
            data-testid="blog-post-breadcrumb"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <h1 data-testid="blog-post-title">{post.title}</h1>
          <div className="blog-meta flex items-center gap-3 flex-wrap -mt-2 mb-8" data-testid="blog-post-meta">
            <span>{formatDate(post.publish_date)}</span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {post.reading_time_minutes || 1} min read
            </span>
            <span aria-hidden>·</span>
            <span>By {post.author || "Regulens Team"}</span>
          </div>

          <div
            data-testid="blog-post-body"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
          />

          {/* CTA block */}
          <div
            className="mt-12 rounded-md p-6"
            style={{ border: "2px solid var(--rg-amber)", background: "var(--rg-bg)" }}
            data-testid="blog-post-cta"
          >
            <h3
              className="text-[var(--rg-text-strong)]"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 22, marginTop: 0, marginBottom: 12 }}
            >
              Check your store's readiness score — free.
            </h3>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "var(--rg-muted)", margin: "0 0 16px 0", lineHeight: 1.6 }}>
              Takes 3 minutes. Not legal advice.
            </p>
            <a href="https://getregulens.com" target="_blank" rel="noopener noreferrer">
              <Button
                className="text-[var(--rg-on-sage)] font-bold"
                style={{ background: "var(--rg-sage)", fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}
                data-testid="blog-post-cta-btn"
              >
                Get my free readiness score →
              </Button>
            </a>
          </div>
        </div>

        {/* Further Reading */}
        {state.further.length > 0 && (
          <section className="max-w-[720px] mx-auto mt-16" data-testid="blog-post-further-reading">
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 22,
                fontWeight: 600,
                color: "var(--rg-sage)",
                marginBottom: 16,
              }}
            >
              Further Reading
            </h2>
            <ul className="space-y-4">
              {state.further.map((r) => (
                <li key={r.slug} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 hover:border-[var(--rg-border-hi)] transition-fast">
                  <Link to={`/blog/${r.slug}`} className="block group" data-testid={`blog-further-${r.slug}`}>
                    <h3
                      className="group-hover:text-[var(--rg-sage)] transition-fast"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 600, color: "var(--rg-text-strong)", margin: 0 }}
                    >
                      {r.title}
                    </h3>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "var(--rg-border)", marginTop: 8, lineHeight: 1.6 }}>
                      {r.excerpt}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </Layout>
  );
}
